import { Switch } from "@/components/ui/switch";
import { ARCH_STYLES, badRating, GRID_PATTERNS, GRID_TYPES } from "@/lib/pricing";

const sel = "h-8 w-full rounded-md border border-slate-800 bg-slate-900/70 px-2 text-sm text-slate-100 disabled:opacity-40";
const Lbl = ({ children }) => <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">{children}</span>;
export const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function OptionSelect({ label, testId, value, options, onChange }) {
  const names = options.map((o) => o.name);
  return (
    <label className="block">
      <Lbl>{label}</Lbl>
      <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className={sel}>
        <option value="">—</option>
        {value && !names.includes(value) && <option value={value}>{value} (saved)</option>}
        {names.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    </label>
  );
}

function Rating({ label, testId, value, onChange }) {
  const bad = badRating(value);
  return (
    <label className="block">
      <Lbl>{label}</Lbl>
      <input data-testid={testId} inputMode="decimal" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="e.g. 0.30"
        className={`h-8 w-full rounded-md border bg-slate-900/70 px-2 text-right font-mono-num text-sm text-slate-100 outline-none focus:border-amber-500/70 ${bad ? "border-red-500/70" : "border-slate-800"}`} />
      {bad && <span data-testid={`${testId.replace("-input", "-error")}`} className="mt-1 block text-[11px] text-red-400">Enter a number 0 or greater</span>}
    </label>
  );
}

export function GlassFields({ row, idx, options = {}, onChange, prefix = "quote-item", suffix }) {
  const sfx = suffix ?? `-${idx}`;
  const types = row.glass_types || [];
  const known = (options.glass_types || []).map((o) => o.name);
  const chips = [...known, ...types.filter((t) => !known.includes(t))];
  const toggle = (n) => onChange({ glass_types: types.includes(n) ? types.filter((x) => x !== n) : [...types, n] });
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
      <OptionSelect label="Glazing" testId={`${prefix}-glazing-select${sfx}`} value={row.glazing} options={options.glazing || []} onChange={(v) => onChange({ glazing: v })} />
      <OptionSelect label="Gas fill" testId={`${prefix}-gas-select${sfx}`} value={row.gas} options={options.gas || []} onChange={(v) => onChange({ gas: v })} />
      <OptionSelect label="Thickness / spacer" testId={`${prefix}-thickness-select${sfx}`} value={row.thickness} options={options.thickness || []} onChange={(v) => onChange({ thickness: v })} />
      <Rating label="U-factor" testId={`${prefix}-u-value-input${sfx}`} value={row.u_value} onChange={(v) => onChange({ u_value: v })} />
      <Rating label="SHGC" testId={`${prefix}-shgc-input${sfx}`} value={row.shgc} onChange={(v) => onChange({ shgc: v })} />
      <div className="col-span-2 md:col-span-4 xl:col-span-6">
        <Lbl>Glass type (pick any)</Lbl>
        <div className="flex flex-wrap gap-2">
          {chips.map((n) => {
            const on = types.includes(n);
            return (
              <button key={n} type="button" data-testid={`${prefix}-glass-type-${slug(n)}${sfx}`} aria-pressed={on} onClick={() => toggle(n)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-amber-500/70 bg-amber-500/15 text-amber-200" : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200"}`}>
                {n}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function GridFields({ row, onChange, prefix = "quote-item", sfx = "" }) {
  const on = !!row.grids;
  const num = (k) => (
    <label className="block w-20">
      <Lbl>{k === "grid_rows" ? "Rows" : "Cols"}</Lbl>
      <input data-testid={`${prefix}-${k.replace("_", "-")}-input${sfx}`} type="number" min={1} max={12} disabled={!on}
        value={row[k] ?? ""} onChange={(e) => onChange({ [k]: e.target.value })} className={`${sel} text-right font-mono-num`} />
    </label>
  );
  return (
    <div className="flex flex-wrap items-end gap-4">
      <label className="flex items-center gap-2 pb-1.5">
        <Switch data-testid={`${prefix}-grids-toggle${sfx}`} checked={on} onCheckedChange={(v) => onChange({ grids: v })} />
        <span className="text-sm text-slate-300">Grids</span>
      </label>
      <label className="block w-36">
        <Lbl>Pattern</Lbl>
        <select data-testid={`${prefix}-grid-pattern-select${sfx}`} disabled={!on} value={row.grid_pattern || "colonial"}
          onChange={(e) => onChange({ grid_pattern: e.target.value })} className={sel}>
          {GRID_PATTERNS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      {(row.grid_pattern || "colonial") === "colonial" && <>{num("grid_rows")}{num("grid_cols")}</>}
      <label className="block w-56">
        <Lbl>Grid type</Lbl>
        <select data-testid={`${prefix}-grid-type-select${sfx}`} disabled={!on} value={row.grid_type || "SDL"}
          onChange={(e) => onChange({ grid_type: e.target.value })} className={sel}>
          {GRID_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <label className="block w-36">
        <Lbl>Grid color</Lbl>
        <input data-testid={`${prefix}-grid-color-input${sfx}`} disabled={!on} value={row.grid_color || ""} placeholder={row.ext_color || "e.g. White"}
          onChange={(e) => onChange({ grid_color: e.target.value })} className={sel} />
      </label>
    </div>
  );
}

export function ArchFields({ row, idx, onChange }) {
  return (
    <div className="flex flex-wrap items-end gap-4">
      <label className="flex items-center gap-2 pb-1.5">
        <Switch data-testid={`quote-item-arch-toggle-${idx}`} checked={!!row.arch} onCheckedChange={(v) => onChange({ arch: v })} />
        <span className="text-sm text-slate-300">Arch top</span>
      </label>
      <label className="block w-48">
        <Lbl>Arch style</Lbl>
        <select data-testid={`quote-item-arch-style-select-${idx}`} disabled={!row.arch} value={row.arch_style || "half_round"}
          onChange={(e) => onChange({ arch_style: e.target.value })} className={sel}>
          {ARCH_STYLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
    </div>
  );
}
